import React, { useContext, useEffect, useState } from 'react';
import { FetchCallbacksContext } from '../../FetchCallbacks';
import { FormBuilderContext } from '../../../components/engine/FormBuilderEngine';
import FormBuilderDocusignView from '../view/FormBuilderDocusignView';
import useFetchWithCallbacks from '../../../hooks/useFetchWithCallbacks';
import useTranslations from '../../../hooks/useTranslations';

const FormBuilderDocusignEngine = props => {
  const {
    formId,
    pageId,
    form = {},
    goBackToIntegrationsPage,
    integrationData: receivedIntegrationData,
    updatedTemplate = false
  } = props;
  const fetchAPI = useFetchWithCallbacks();
  const t = useTranslations();
  const { openSnackbar } = useContext(FetchCallbacksContext);

  const dropdownFullOption = {
    value: 'full-submission',
    'data-testid': 'full-submission',
    text: 'Full Submission'
  };

  const reportData =
    receivedIntegrationData &&
    receivedIntegrationData.additionalParams &&
    receivedIntegrationData.additionalParams.reportData
      ? receivedIntegrationData.additionalParams.reportData
      : {};

  const [integrationData, setIntegrationData] = useState(
    receivedIntegrationData
  );
  const [dropdownOptions, setDropdownOptions] = useState([dropdownFullOption]);
  const [dropdownValue, setDropdownValue] = useState(
    reportData.template || 'full-submission'
  );
  const [includeLogo, setIncludeLogo] = useState(typeof reportData.includeLogo !== 'undefined' && !reportData.includeLogo ? false : true);
  const [includeBlanks, setIncludeBlanks] = useState(typeof reportData.includeBlanks === 'undefined' || !reportData.includeBlanks ? false : true);
  const [docusignConnected, setDocusignConnected] = useState(
    integrationData.id && integrationData.id !== 'new'
  );
  const [initialized, setInitialized] = useState(false);

  const clickedSign = () => {
    let state = {formId: formId};
    if (docusignConnected) {
      return;
    }
    fetchAPI(
      '/scheduler/v1/ds/url',
      {
        handleResponse: r =>
          r && r.headers && r.headers.map ? r.headers.map.location : ''
      }
    ).then(res => {
      let decoded = decodeURI(res);
      let newUrl = `${decoded}&state=${JSON.stringify(state)}`;
      if (window.location.host.indexOf(':3000') !== -1 && newUrl.indexOf('omnitrack-test.vinciworks-dev.com') !== -1) {
        newUrl = newUrl.replace('https%3A%2F%2Fomnitrack-test.vinciworks-dev.com', 'http%3A%2F%2Fomnidev.localdomain.com:3000');
      }
      window.location.href = newUrl;
    });
  };

  const resetFormToDefaults = () => {
    setDocusignConnected(false);
    setIntegrationData({ id: 'new' });
    setDropdownValue('full-submission');
    setIncludeLogo(true);
    setIncludeBlanks(true);
  };

  const clickedDisconnect = () => {
    if (!integrationData || !integrationData.id) {
      return;
    }
    if (integrationData.id === 'new') {
      resetFormToDefaults();
      return;
    }
    fetchAPI(`/scheduler/v1/ds/disconnect/${integrationData.id}`, {
      method: 'PUT'
    })
      .then(res => {
        resetFormToDefaults();
        openSnackbar(
          'success',
          'snackbars.integrations.docusign_disconnect_success'
        );
      })
      .catch(() => {
        openSnackbar(
          'failure',
          'snackbars.integrations.docusign_disconnect_failure'
        );
      });
  };

  const saveTemplate = (callback = () => {}) => {
    let body, method, url;
    if (!integrationData || !integrationData.id) {
      return;
    }
    method = 'PUT';
    url = `/scheduler/v1/integration/${integrationData.id}`;
    body = {...integrationData};
    body.active = true;
    body.additionalParams = {
      reportData: {
        template: dropdownValue,
        includeBlanks: includeBlanks,
        includeLogo: includeLogo
      }
    };
    fetchAPI(url, {
      method: method,
      body: body
    }).then(res => {
      reportData.template = dropdownValue;
      openSnackbar(
        'success',
        'snackbars.integrations.docusign_changes_saved'
      );
      callback();
    });
  };

  const setDropdownValueWrapper = val => {
    if (val !== dropdownValue) {
      setDropdownValue(val);
    }
  };

  const clickedSave = () => {
    saveTemplate(() => {
      goBackToIntegrationsPage({skipConfirm: true});
    });
  };

  useEffect(() => {
    if (!formId) {
      return;
    }
    const reportsQuery =
      `[[{"field": "'formId'", "op": "=", "value": "` +
      formId +
      `"},{"field": "'reportType'", "op": "=", "value": "single" }]]`;
    fetchAPI('/forms/v1/eu/reports?query=' + reportsQuery).then(res => {
      let newOptionsList = [dropdownFullOption];
      if (!res || !res.results) {
        return;
      }
      res.results.forEach(one => {
        newOptionsList.push({
          value: one.id,
          'data-testid': one.title,
          text: one.title
        });
      });
      setDropdownOptions(newOptionsList);
      setDropdownValue(reportData.template || 'full-submission');
      if (updatedTemplate) {
        saveTemplate();
      }
    });
  }, []);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      return;
    }
    if (docusignConnected && initialized) {
      saveTemplate();
    }
  }, [dropdownValue, includeLogo, includeBlanks]);

  const viewProps = {
    clickedDisconnect,
    clickedSave,
    dropdownOptions,
    dropdownValue,
    formId,
    includeLogo,
    includeBlanks,
    setIncludeLogo,
    setIncludeBlanks,
    docusignConnected,
    clickedSign,
    setDropdownValue: setDropdownValueWrapper,
    alreadyEnabled: reportData && reportData.template ? true : false
  };

  return <FormBuilderDocusignView {...viewProps} />;
};

export default FormBuilderDocusignEngine;
